import React from 'react';
import styled from 'styled-components';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { colors } from '@/constants/colors';
import {
   getIspAccounts,
   disconnectIspAccount,
   deleteIspAccount,
   type IspAccountRow,
} from '@/api/isp';

/**
 * Deleting a customer who still holds connections.
 *
 * 🔴 THE OLD MESSAGE POINTED AT A BUTTON THAT NO LONGER EXISTS. It said
 * "Disconnect those first" — but Disconnect was taken off the Accounts page
 * (Ray, 2026-09-03: *"in accounts please remove that disconnect to avoid those
 * logs dialing"*), so the operator was told to do something the product had
 * stopped offering, and pressing "Yes, delete" answered 409 anyway.
 *
 * Ray's fix, 2026-09-05: *"when they delete the customer it show a modal popup
 * and will say it that this customer has live account something then in the
 * modal it has list of the accounts list, and it has disconnect button, so
 * after he disconnects those items the delete will enable on the popup modal."*
 *
 * So the dead end becomes the place the work gets done: the lines are listed,
 * each can be disconnected from here, and Delete unlocks by itself once none
 * are left.
 */

/**
 * 🔴 EVERY LINE BLOCKS, NOT JUST THE LIVE ONES.
 *
 * This used to read `status !== 'disconnected'`, so the modal unlocked Delete
 * once every line was disconnected — while the server counts EVERY account
 * whatever its status and kept refusing. The screen said "can be deleted" and
 * the API said no, in a loop with no way out. Ray, 2026-09-09, watching an
 * operator hit it: *"he is trap."*
 *
 * The two now share one rule: a line has to be REMOVED, not merely disconnected.
 */
const stillCounts = (_a: IspAccountRow) => true;
/** Can this line be removed yet? The server requires disconnected first. */
const canRemove = (a: IspAccountRow) => a.status === 'disconnected';

interface Props {
   customerId: string;
   customerName: string;
   onCancel: () => void;
   onDeleted: () => void;
   onDelete: (id: string) => Promise<unknown>;
}

const IspCustomerDelete: React.FC<Props> = ({
   customerId,
   customerName,
   onCancel,
   onDeleted,
   onDelete,
}) => {
   const qc = useQueryClient();

   const { data, isLoading } = useQuery({
      queryKey: ['isp-accounts', customerId],
      queryFn: () => getIspAccounts(customerId),
   });
   const accounts = data?.accounts ?? [];
   const live = accounts.filter(stillCounts);

   const cut = useMutation({
      mutationFn: (id: string) => disconnectIspAccount(id),
      onSuccess: (r) => {
         qc.invalidateQueries({ queryKey: ['isp-accounts', customerId] });
         qc.invalidateQueries({ queryKey: ['isp-customers'] });
         // The router write can fail while our record moves — say which.
         if (r?.warning) toast.warning(r.warning);
         else toast.success('Disconnected.');
      },
      onError: (e: unknown) =>
         toast.error(
            (e as { response?: { data?: { error?: string } } })?.response?.data
               ?.error ?? 'Could not disconnect that line.'
         ),
   });

   const drop = useMutation({
      mutationFn: (id: string) => deleteIspAccount(id),
      onSuccess: (r) => {
         qc.invalidateQueries({ queryKey: ['isp-accounts', customerId] });
         qc.invalidateQueries({ queryKey: ['isp-customers'] });
         if (r?.warning) toast.warning(r.warning);
         else toast.success('Line removed.');
      },
      onError: (e: unknown) =>
         toast.error(
            (e as { response?: { data?: { error?: string } } })?.response?.data
               ?.error ?? 'Could not remove that line.'
         ),
   });

   const remove = useMutation({
      mutationFn: () => onDelete(customerId),
      onSuccess: () => {
         toast.success(`${customerName} deleted.`);
         onDeleted();
      },
      onError: (e: unknown) =>
         toast.error(
            (e as { response?: { data?: { error?: string } } })?.response?.data
               ?.error ?? 'Could not delete this customer.'
         ),
   });

   const busy = cut.isPending || drop.isPending || remove.isPending;

   return (
      <Wrap>
         <Lead>
            Deleting <strong>{customerName}</strong> takes their payment history
            with them. If they have simply left, setting them to Disconnected
            keeps the record.
         </Lead>

         {isLoading ? (
            <Muted>Checking their connections…</Muted>
         ) : live.length === 0 ? (
            <Ok>
               {accounts.length === 0
                  ? 'They hold no connections.'
                  : 'Every connection has been removed. This customer can be deleted.'}
            </Ok>
         ) : (
            <>
               <Warn>
                  {live.length === 1
                     ? 'This customer still holds one connection.'
                     : `This customer still holds ${live.length} connections.`}{' '}
                  Disconnect and remove {live.length === 1 ? 'it' : 'them'} here
                  first — a disconnected line still counts until it is removed.
               </Warn>

               <List>
                  {live.map((a) => (
                     <Row key={a.id}>
                        <RowMain>
                           <Login>{a.pppoeUsername}</Login>
                           <Sub>{a.serverName}</Sub>
                        </RowMain>
                        <Status $s={a.status}>{a.status}</Status>
                        {/* Two steps, in the order the server enforces:
                            disconnect ends the service, remove clears the
                            record. A live line can never go in one click. */}
                        {canRemove(a) ? (
                           <Cut
                              type="button"
                              $danger
                              disabled={busy}
                              onClick={() => drop.mutate(a.id)}
                           >
                              {drop.isPending && drop.variables === a.id
                                 ? 'Removing…'
                                 : 'Remove line'}
                           </Cut>
                        ) : (
                           <Cut
                              type="button"
                              disabled={busy}
                              onClick={() => cut.mutate(a.id)}
                           >
                              {cut.isPending && cut.variables === a.id
                                 ? 'Disconnecting…'
                                 : 'Disconnect'}
                           </Cut>
                        )}
                     </Row>
                  ))}
               </List>
            </>
         )}

         <Actions>
            <Ghost type="button" onClick={onCancel} disabled={busy}>
               Cancel
            </Ghost>
            {/*
             * 🔴 DISABLED, NOT HIDDEN. An operator has to see that Delete is
             * the thing waiting on them — a button that appears out of nowhere
             * once the last line goes is a button they will not notice.
             */}
            <Danger
               type="button"
               disabled={busy || isLoading || live.length > 0}
               onClick={() => remove.mutate()}
            >
               {remove.isPending ? 'Deleting…' : 'Delete customer'}
            </Danger>
         </Actions>
      </Wrap>
   );
};

export default IspCustomerDelete;

const Wrap = styled.div`
   display: flex;
   flex-direction: column;
   gap: 14px;
   padding: 4px 2px 0;
`;
const Lead = styled.p`
   margin: 0;
   font-size: 13.5px;
   line-height: 1.6;
   color: ${colors.GRAY3};
`;
const Muted = styled.p`
   margin: 0;
   font-size: 13px;
   color: ${colors.GRAY5};
`;
const Warn = styled.p`
   margin: 0;
   font-size: 13px;
   line-height: 1.6;
   font-weight: 600;
   color: #B45309;
`;
const Ok = styled.p`
   margin: 0;
   font-size: 13px;
   font-weight: 600;
   color: ${colors.SUCCESS};
`;
const List = styled.div`
   display: flex;
   flex-direction: column;
   border: 1px solid ${colors.GRAY12};
   border-radius: 10px;
   overflow: hidden;
   max-height: 260px;
   overflow-y: auto;
`;
const Row = styled.div`
   display: flex;
   align-items: center;
   gap: 10px;
   padding: 10px 12px;
   border-bottom: 1px solid ${colors.GRAY12};
   &:last-child {
      border-bottom: none;
   }

   @media (max-width: 560px) {
      flex-wrap: wrap;
   }
`;
const RowMain = styled.div`
   flex: 1;
   min-width: 0;
`;
const Login = styled.div`
   font-family: monospace;
   font-size: 13px;
   font-weight: 700;
   color: ${colors.GRAY1};
`;
const Sub = styled.div`
   font-size: 11.5px;
   color: ${colors.GRAY5};
`;
const Status = styled.span<{ $s: string }>`
   font-size: 11px;
   font-weight: 700;
   text-transform: capitalize;
   color: ${({ $s }) =>
      $s === 'active'
         ? colors.SUCCESS
         : $s === 'suspended'
           ? '#B45309'
           : colors.GRAY5};
`;
const Cut = styled.button<{ $danger?: boolean }>`
   font-family: inherit;
   font-size: 12px;
   font-weight: 700;
   padding: 6px 12px;
   border-radius: 8px;
   border: 1px solid ${({ $danger }) => ($danger ? colors.DANGER : colors.GRAY12)};
   background: ${colors.WHITE};
   color: ${({ $danger }) => ($danger ? colors.DANGER : colors.GRAY1)};
   cursor: pointer;
   white-space: nowrap;

   &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
   }
`;
const Actions = styled.div`
   display: flex;
   justify-content: flex-end;
   gap: 8px;
   padding-top: 4px;
`;
const Ghost = styled.button`
   font-family: inherit;
   font-size: 13px;
   font-weight: 700;
   padding: 9px 16px;
   border-radius: 10px;
   border: 1px solid ${colors.GRAY12};
   background: ${colors.WHITE};
   color: ${colors.GRAY1};
   cursor: pointer;
   &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
   }
`;
const Danger = styled.button`
   font-family: inherit;
   font-size: 13px;
   font-weight: 700;
   padding: 9px 16px;
   border-radius: 10px;
   border: none;
   background: ${colors.DANGER};
   color: ${colors.WHITE};
   cursor: pointer;

   &:disabled {
      opacity: 0.45;
      cursor: not-allowed;
   }
`;
